import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';

const FALLBACK_IMAGE = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" fill="#f3f4f6"><rect width="800" height="600"/><text x="400" y="300" text-anchor="middle" fill="#a0aec0" font-family="system-ui" font-size="18">Restaurant Image</text></svg>`)}`;

interface RestaurantData {
    id: string;
    name: string;
    slug?: string;
    description: { en: string; sv: string; da?: string };
    image: string;
    gallery?: string[];
    videoUrl?: string;
    cuisine: string;
    priceRange: string;
    rating?: number;
    address?: string;
    website?: string;
    phone?: string;
    openingHours?: string;
    reservationEnabled?: boolean;
    aiComment?: { en: string; sv: string; da?: string };
    coordinates?: { lat: number; lng: number };
    mapUrl?: string;
    tripAdvisorUrl?: string;
    destinationName?: string;
    destinationSlug?: string;
    contentFallback?: boolean;
    destinationContext?: {
        history?: { en?: string; sv?: string; da?: string };
        nature?: { en?: string; sv?: string; da?: string };
    };
}

export const RestaurantPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { language } = useLocalization();
    const [restaurant, setRestaurant] = useState<RestaurantData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeImage, setActiveImage] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [showReservationForm, setShowReservationForm] = useState(false);
    const [reservationForm, setReservationForm] = useState({ name: '', email: '', phone: '', date: '', time: '', guests: 2, notes: '' });
    const [reservationSent, setReservationSent] = useState(false);
    const [aiReview, setAiReview] = useState<any>(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [nearbyHotels, setNearbyHotels] = useState<any[]>([]);
    const [nearbyHotelsLoading, setNearbyHotelsLoading] = useState(false);

    const getText = (val: any): string => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
            // Try language-specific value, then fallbacks
            const resolved = val[language] || val.en || val.sv || val.da;
            if (!resolved) return '';
            // If resolved value is still an object, recurse
            if (typeof resolved === 'string') return resolved;
            if (typeof resolved === 'object') return getText(resolved);
        }
        return String(val);
    };

    useEffect(() => {
        const fetchRestaurant = async () => {
            setLoading(true);
            try {
                const response = await fetch(`/api/restaurants/${slug}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.restaurant) {
                        setRestaurant(data.restaurant);
                    } else {
                        setError('Restaurant not found');
                    }
                } else {
                    setError('Restaurant not found');
                }
            } catch (e) {
                setError('Failed to load restaurant');
            }
            setLoading(false);
        };
        fetchRestaurant();
    }, [slug]);

    // Auto-load Petra review
    useEffect(() => {
        if (restaurant && slug) {
            fetch(`/api/restaurants/${slug}/ai-review?lang=${language}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    if (data?.success) setAiReview(data.review);
                })
                .catch(() => {});
        }
    }, [restaurant, slug, language]);

    // Auto-load city hotels via live Tourvisio API
    useEffect(() => {
        if (restaurant?.destinationName) {
            setNearbyHotelsLoading(true);
            const city = restaurant.destinationName;
            fetch(`/api/hotel-cache/city-hotels?city=${encodeURIComponent(city)}&limit=6`)
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    if (data?.success) setNearbyHotels(data.hotels || []);
                })
                .catch(() => {})
                .finally(() => setNearbyHotelsLoading(false));
        }
    }, [restaurant?.destinationName]);

    // SEO
    useEffect(() => {
        if (restaurant) {
            document.title = `${restaurant.name} | Lumi Tours`;
        }
        return () => { document.title = 'Lumi Tours'; };
    }, [restaurant]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (error || !restaurant) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
                <span className="material-icons-round text-6xl text-gray-300">restaurant</span>
                <h1 className="text-2xl font-bold text-gray-700">{language === 'sv' ? 'Restaurangen hittades inte' : language === 'da' ? 'Restauranten blev ikke fundet' : 'Restaurant not found'}</h1>
                <button onClick={() => navigate(-1)} className="px-6 py-2 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors">
                    {language === 'sv' ? '← Tillbaka' : language === 'da' ? '← Tilbage' : '← Go back'}
                </button>
            </div>
        );
    }

    const allImages = [restaurant.image, ...(restaurant.gallery || [])]
        .filter(Boolean)
        .filter(url => !url.includes('maps.googleapis.com/maps/api/place/photo'));
    const desc = getText(restaurant.description);
    const aiComment = getText(restaurant.aiComment);

    const handleReservationSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setReservationSent(true);
        setTimeout(() => {
            setShowReservationForm(false);
            setReservationSent(false);
        }, 3000);
    };

    const openLightbox = (index: number) => {
        setActiveImage(index);
        setLightboxOpen(true);
    };

    return (
        <div className="min-h-screen bg-white overflow-x-hidden">
            {/* Hero Section — fixed overflow */}
            <div className="relative w-full h-[45vh] sm:h-[50vh] md:h-[60vh] overflow-hidden">
                <img
                    src={allImages[activeImage] || FALLBACK_IMAGE}
                    alt={restaurant.name}
                    className="w-full h-full object-cover transition-opacity duration-500 cursor-pointer"
                    onClick={() => openLightbox(activeImage)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors z-10 text-sm"
                >
                    <span className="material-icons-round text-sm">arrow_back</span>
                    {language === 'sv' ? 'Tillbaka' : language === 'da' ? 'Tilbage' : 'Back'}
                </button>

                {/* Image count badge */}
                {allImages.length > 1 && (
                    <button
                        onClick={() => openLightbox(activeImage)}
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors z-10 text-sm"
                    >
                        <span className="material-icons-round text-sm">collections</span>
                        {allImages.length} {language === 'sv' ? 'bilder' : language === 'da' ? 'billeder' : 'photos'}
                    </button>
                )}

                {/* Hero Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-12">
                    <div className="container mx-auto max-w-5xl">
                        <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4">
                            <span className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                                {restaurant.cuisine}
                            </span>
                            <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full">
                                {restaurant.priceRange}
                            </span>
                            {restaurant.rating && (
                                <span className="flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full">
                                    <span className="material-icons-round text-amber-400 text-sm">star</span>
                                    {restaurant.rating}
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-1 sm:mb-2 break-words">{restaurant.name}</h1>
                        {restaurant.destinationName && (
                            <p className="text-white/70 text-base sm:text-lg flex items-center gap-2">
                                <span className="material-icons-round text-sm">location_on</span>
                                {restaurant.destinationName}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Image Gallery Thumbnails */}
            {allImages.length > 1 && (
                <div className="bg-gray-100 py-3 sm:py-4 overflow-hidden">
                    <div className="container mx-auto max-w-5xl px-4">
                        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                            {allImages.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => { setActiveImage(i); }}
                                    className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg sm:rounded-xl overflow-hidden border-2 transition-all ${
                                        i === activeImage ? 'border-primary scale-105 shadow-lg' : 'border-transparent opacity-70 hover:opacity-100'
                                    }`}
                                >
                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content — fixed overflow */}
            <div className="container mx-auto max-w-5xl px-4 py-8 sm:py-12 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
                    {/* Left Column - Description & Details */}
                    <div className="lg:col-span-2 space-y-6 sm:space-y-8 min-w-0">
                        {/* Description */}
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                                {language === 'sv' ? 'Om restaurangen' : language === 'da' ? 'Om restauranten' : 'About'}
                            </h2>
                            {desc.includes('<') && desc.includes('>') ? (
                                <div className="text-gray-600 leading-relaxed text-base sm:text-lg break-words prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: desc }} />
                            ) : (
                                <p className="text-gray-600 leading-relaxed text-base sm:text-lg break-words">{desc}</p>
                            )}
                        </div>

                        {/* Destination CTA Banner — shown for restaurants with auto-generated content */}
                        {restaurant.contentFallback && restaurant.destinationSlug && (
                            <div
                                className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-primary/10 via-teal-50 to-blue-50 border border-primary/20 p-5 sm:p-6 cursor-pointer group hover:shadow-lg transition-all"
                                onClick={() => navigate(`/destination/${restaurant.destinationSlug}`)}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-primary/15 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <span className="material-icons-round text-primary text-2xl">explore</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                                            {language === 'sv'
                                                ? `Upptäck ${restaurant.destinationName}`
                                                : language === 'da'
                                                    ? `Opdag ${restaurant.destinationName}`
                                                    : `Discover ${restaurant.destinationName}`}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {language === 'sv'
                                                ? 'Utforska hotell, aktiviteter och mer om denna destination'
                                                : language === 'da'
                                                    ? 'Udforsk hoteller, aktiviteter og mere om denne destination'
                                                    : 'Explore hotels, activities and more about this destination'}
                                        </p>
                                    </div>
                                    <span className="material-icons-round text-primary text-xl self-center group-hover:translate-x-1 transition-transform">
                                        arrow_forward
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* AI Comment */}
                        {aiComment && (
                            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 sm:p-6 border border-indigo-100">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="material-icons-round text-indigo-600">auto_awesome</span>
                                    <span className="text-sm font-bold text-indigo-700">
                                        {language === 'sv' ? 'Petras Rekommendation' : language === 'da' ? 'Petras Anbefaling' : "Petra's Recommendation"}
                                    </span>
                                </div>
                                <div className="text-indigo-900/80 leading-relaxed break-words prose prose-indigo max-w-none" dangerouslySetInnerHTML={{ __html: aiComment }} />
                            </div>
                        )}

                        {/* Photo Gallery Grid (when more than 3 images) */}
                        {allImages.length > 3 && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                                    {language === 'sv' ? 'Galleri' : language === 'da' ? 'Galleri' : 'Gallery'}
                                </h2>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                    {allImages.slice(1).map((img, i) => (
                                        <button
                                            key={i}
                                            onClick={() => openLightbox(i + 1)}
                                            className="relative aspect-[4/3] rounded-xl overflow-hidden group"
                                        >
                                            <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Map Section */}
                        {restaurant.coordinates?.lat && restaurant.coordinates?.lng && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                                    {language === 'sv' ? 'Hitta hit' : language === 'da' ? 'Find vej' : 'Location'}
                                </h2>
                                <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100 border border-gray-200">
                                    <iframe
                                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${restaurant.coordinates.lat},${restaurant.coordinates.lng}&zoom=15`}
                                        title={`${restaurant.name} location`}
                                        className="w-full h-full border-0"
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                                {restaurant.address && (
                                    <a
                                        href={(restaurant as any).mapUrl || `https://www.google.com/maps/search/?api=1&query=${restaurant.coordinates.lat},${restaurant.coordinates.lng}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                                    >
                                        <span className="material-icons-round text-sm">directions</span>
                                        {language === 'sv' ? 'Öppna i Google Maps' : language === 'da' ? 'Åbn i Google Maps' : 'Open in Google Maps'}
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Video */}
                        {restaurant.videoUrl && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                                    {language === 'sv' ? 'Video' : 'Video'}
                                </h2>
                                <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100">
                                    <iframe
                                        src={restaurant.videoUrl}
                                        title={restaurant.name}
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        )}

                        {/* AI Reviews Section */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                                    {language === 'sv' ? 'Petras Översikt' : language === 'da' ? 'Petras Oversigt' : "Petra's Review"}
                                </h2>
                                {!aiReview && (
                                    <button
                                        onClick={async () => {
                                            setReviewLoading(true);
                                            try {
                                                const res = await fetch(`/api/restaurants/${slug}/ai-review`);
                                                if (res.ok) {
                                                    const data = await res.json();
                                                    if (data.success) setAiReview(data.review);
                                                }
                                            } catch(e) {}
                                            setReviewLoading(false);
                                        }}
                                        disabled={reviewLoading}
                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all disabled:opacity-50"
                                    >
                                        <span className="material-icons-round text-sm">{reviewLoading ? 'hourglass_empty' : 'auto_awesome'}</span>
                                        {reviewLoading 
                                            ? (language === 'sv' ? 'Genererar...' : language === 'da' ? 'Genererer...' : 'Generating...') 
                                            : (language === 'sv' ? 'Visa Petras recension' : language === 'da' ? 'Vis Petras anmeldelse' : "Show Petra's Review")}
                                    </button>
                                )}
                            </div>

                            {aiReview && (
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 sm:p-6 border border-indigo-100">
                                        <div className="text-indigo-900/80 leading-relaxed prose prose-indigo max-w-none" dangerouslySetInnerHTML={{ __html: getText(aiReview.summary) }} />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {aiReview.atmosphere && (
                                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="material-icons-round text-amber-600 text-sm">emoji_emotions</span>
                                                    <span className="text-sm font-bold text-amber-800">
                                                        {language === 'sv' ? 'Atmosfär' : language === 'da' ? 'Atmosfære' : 'Atmosphere'}
                                                    </span>
                                                </div>
                                                <p className="text-amber-900/70 text-sm">{getText(aiReview.atmosphere)}</p>
                                            </div>
                                        )}

                                        {((typeof aiReview.mustTry === 'object' && !Array.isArray(aiReview.mustTry)) ? (aiReview.mustTry[language] || aiReview.mustTry.en) : aiReview.mustTry)?.length > 0 && (
                                            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="material-icons-round text-green-600 text-sm">restaurant_menu</span>
                                                    <span className="text-sm font-bold text-green-800">
                                                        {language === 'sv' ? 'Måste prova' : language === 'da' ? 'Skal prøves' : 'Must Try'}
                                                    </span>
                                                </div>
                                                <ul className="text-green-900/70 text-sm space-y-1">
                                                    {((typeof aiReview.mustTry === 'object' && !Array.isArray(aiReview.mustTry)) ? (aiReview.mustTry[language] || aiReview.mustTry.en) : aiReview.mustTry).map((item: string, i: number) => (
                                                        <li key={i}>• {item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {((typeof aiReview.pros === 'object' && !Array.isArray(aiReview.pros)) ? (aiReview.pros[language] || aiReview.pros.en) : aiReview.pros)?.length > 0 && (
                                            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="material-icons-round text-emerald-600 text-sm">thumb_up</span>
                                                    <span className="text-sm font-bold text-emerald-800">
                                                        {language === 'sv' ? 'Fördelar' : language === 'da' ? 'Fordele' : 'Pros'}
                                                    </span>
                                                </div>
                                                <ul className="text-emerald-900/70 text-sm space-y-1">
                                                    {((typeof aiReview.pros === 'object' && !Array.isArray(aiReview.pros)) ? (aiReview.pros[language] || aiReview.pros.en) : aiReview.pros).map((item: string, i: number) => (
                                                        <li key={i}>✓ {item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {((typeof aiReview.cons === 'object' && !Array.isArray(aiReview.cons)) ? (aiReview.cons[language] || aiReview.cons.en) : aiReview.cons)?.length > 0 && (
                                            <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="material-icons-round text-red-600 text-sm">info</span>
                                                    <span className="text-sm font-bold text-red-800">
                                                        {language === 'sv' ? 'Att tänka på' : language === 'da' ? 'Ting at overveje' : 'Things to Consider'}
                                                    </span>
                                                </div>
                                                <ul className="text-red-900/70 text-sm space-y-1">
                                                    {((typeof aiReview.cons === 'object' && !Array.isArray(aiReview.cons)) ? (aiReview.cons[language] || aiReview.cons.en) : aiReview.cons).map((item: string, i: number) => (
                                                        <li key={i}>• {item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Contact & Details Card */}
                    <div className="space-y-5 sm:space-y-6 min-w-0">
                        {/* Contact Card */}
                        <div className="bg-gray-50 rounded-2xl p-5 sm:p-6 border border-gray-100 space-y-4 sm:space-y-5">
                            <h3 className="text-lg font-bold text-gray-900">
                                {language === 'sv' ? 'Kontaktinformation' : language === 'da' ? 'Kontaktoplysninger' : 'Contact Info'}
                            </h3>

                            {restaurant.address && (
                                <div className="flex items-start gap-3">
                                    <span className="material-icons-round text-primary mt-0.5 flex-shrink-0">location_on</span>
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">{language === 'sv' ? 'Adress' : language === 'da' ? 'Adresse' : 'Address'}</p>
                                        <p className="text-gray-800 font-medium break-words">{restaurant.address}</p>
                                    </div>
                                </div>
                            )}

                            {restaurant.phone && (
                                <div className="flex items-start gap-3">
                                    <span className="material-icons-round text-primary mt-0.5 flex-shrink-0">phone</span>
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">{language === 'sv' ? 'Telefon' : language === 'da' ? 'Telefon' : 'Phone'}</p>
                                        <a href={`tel:${restaurant.phone}`} className="text-primary font-medium hover:underline">{restaurant.phone}</a>
                                    </div>
                                </div>
                            )}

                            {restaurant.website && (
                                <div className="flex items-start gap-3">
                                    <span className="material-icons-round text-primary mt-0.5 flex-shrink-0">language</span>
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">{language === 'sv' ? 'Webbplats' : language === 'da' ? 'Hjemmeside' : 'Website'}</p>
                                        <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline break-all text-sm">
                                            {restaurant.website.replace(/^https?:\/\//, '')}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {restaurant.openingHours && (
                                <div className="flex items-start gap-3">
                                    <span className="material-icons-round text-primary mt-0.5 flex-shrink-0">schedule</span>
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">{language === 'sv' ? 'Öppettider' : language === 'da' ? 'Åbningstider' : 'Hours'}</p>
                                        {Array.isArray(restaurant.openingHours) ? (
                                            <div className="text-gray-800 font-medium text-sm space-y-0.5">
                                                {restaurant.openingHours.map((h: string, i: number) => (
                                                    <p key={i} className="break-words">{h}</p>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-800 font-medium break-words">{restaurant.openingHours}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* TripAdvisor Link */}
                        {(restaurant as any).tripAdvisorUrl && (
                            <a
                                href={(restaurant as any).tripAdvisorUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 bg-[#34e0a1]/10 rounded-2xl border border-[#34e0a1]/30 hover:bg-[#34e0a1]/20 transition-colors group"
                            >
                                <div className="w-10 h-10 bg-[#34e0a1] rounded-xl flex items-center justify-center flex-shrink-0">
                                    <span className="text-xl">🦉</span>
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-gray-900 text-sm">TripAdvisor</p>
                                    <p className="text-gray-600 text-xs">{language === 'sv' ? 'Se recensioner & betyg' : language === 'da' ? 'Se anmeldelser & vurderinger' : 'See reviews & ratings'}</p>
                                </div>
                                {restaurant.rating && (
                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-lg">
                                        <span className="material-icons-round text-amber-500 text-sm">star</span>
                                        <span className="font-bold text-amber-800 text-sm">{restaurant.rating}</span>
                                    </div>
                                )}
                                <span className="material-icons-round text-[#34e0a1] text-sm group-hover:translate-x-0.5 transition-transform">open_in_new</span>
                            </a>
                        )}

                        {/* Reservation Button */}
                        {restaurant.reservationEnabled && (
                            <button
                                onClick={() => setShowReservationForm(true)}
                                className="w-full py-3 sm:py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary/25"
                            >
                                <span className="material-icons-round">book_online</span>
                                {language === 'sv' ? 'Boka bord' : language === 'da' ? 'Reserver bord' : 'Make a Reservation'}
                            </button>
                        )}

                        {/* Quick Info */}
                        <div className="bg-amber-50 rounded-2xl p-5 sm:p-6 border border-amber-100">
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600 text-sm">{language === 'sv' ? 'Kök' : language === 'da' ? 'Køkken' : 'Cuisine'}</span>
                                    <span className="font-bold text-gray-900 text-right">{restaurant.cuisine}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600 text-sm">{language === 'sv' ? 'Prisklass' : language === 'da' ? 'Prisklasse' : 'Price Range'}</span>
                                    <span className="font-bold text-gray-900">{restaurant.priceRange}</span>
                                </div>
                                {restaurant.rating && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-600 text-sm">{language === 'sv' ? 'Betyg' : language === 'da' ? 'Bedømmelse' : 'Rating'}</span>
                                        <div className="flex items-center gap-1">
                                            <span className="material-icons-round text-amber-500 text-sm">star</span>
                                            <span className="font-bold text-gray-900">{restaurant.rating}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* City Hotels — Sidebar Widget (Live Tourvisio Search) */}
                        {(nearbyHotels.length > 0 || nearbyHotelsLoading) && (
                            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-5 sm:p-6 border border-blue-100">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="material-icons-round text-primary">hotel</span>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {language === 'sv' ? `Hotell i ${restaurant.destinationName}` : language === 'da' ? `Hoteller i ${restaurant.destinationName}` : `Hotels in ${restaurant.destinationName}`}
                                    </h3>
                                </div>
                                <p className="text-xs text-gray-500 mb-4">
                                    {language === 'sv' ? 'Boka boende nära restaurangen — min. 4 nätter' : language === 'da' ? 'Book overnatning nær restauranten — min. 4 nætter' : 'Book accommodation near the restaurant — min. 4 nights'}
                                </p>
                                {nearbyHotelsLoading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="flex gap-3 animate-pulse">
                                                <div className="w-20 h-20 bg-gray-200 rounded-xl flex-shrink-0" />
                                                <div className="flex-1 space-y-2 py-1">
                                                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                                                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                                                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {nearbyHotels.slice(0, 6).map((hotel: any, idx: number) => (
                                            <div
                                                key={hotel.id || idx}
                                                className="flex gap-3 p-2 rounded-xl hover:bg-white/80 transition-all cursor-pointer group border border-transparent hover:border-blue-100 hover:shadow-sm"
                                                onClick={() => {
                                                    // Navigate to onlyhotel with search context
                                                    const params = new URLSearchParams({
                                                        destination: restaurant.destinationName || '',
                                                        checkIn: hotel.checkIn || new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
                                                        nights: String(hotel.nights || 4),
                                                        adults: '2'
                                                    });
                                                    navigate(`/onlyhotel?${params.toString()}`);
                                                }}
                                            >
                                                <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                                                    <img
                                                        src={hotel.imageUrl || FALLBACK_IMAGE}
                                                        alt={hotel.name}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                        onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
                                                    />
                                                    {hotel.stars > 0 && (
                                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5">
                                                            <div className="flex gap-0.5 justify-center">
                                                                {Array.from({ length: Math.min(hotel.stars, 5) }).map((_: any, i: number) => (
                                                                    <span key={i} className="text-amber-400 text-[9px]">★</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-sm text-gray-900 line-clamp-1 group-hover:text-primary transition-colors">
                                                        {hotel.name}
                                                    </p>
                                                    {hotel.concept && (
                                                        <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                                                            {hotel.concept}
                                                        </span>
                                                    )}
                                                    {hotel.description && (
                                                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">
                                                            {(typeof hotel.description === 'object' ? hotel.description.text || '' : String(hotel.description))
                                                                .replace(/<[^>]*>?/gm, '')
                                                                .substring(0, 60)}
                                                        </p>
                                                    )}
                                                    {hotel.price > 0 ? (
                                                        <div className="mt-1 flex items-baseline gap-1">
                                                            <span className="text-sm font-bold text-emerald-600">
                                                                {Math.round(hotel.price).toLocaleString('sv-SE')} {hotel.currency || 'SEK'}
                                                            </span>
                                                            <span className="text-[10px] text-gray-400">
                                                                / {hotel.nights || 4} {language === 'sv' ? 'nätter' : language === 'da' ? 'nætter' : 'nights'}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-blue-500 mt-1 font-medium">
                                                            {language === 'sv' ? 'Sök pris →' : language === 'da' ? 'Søg pris →' : 'Check price →'}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="material-icons-round text-gray-300 group-hover:text-primary text-lg self-center transition-colors">
                                                    chevron_right
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <button
                                    onClick={() => {
                                        const checkIn = new Date();
                                        checkIn.setDate(checkIn.getDate() + 7);
                                        const params = new URLSearchParams({
                                            destination: restaurant.destinationName || '',
                                            checkIn: checkIn.toISOString().split('T')[0],
                                            nights: '4',
                                            adults: '2'
                                        });
                                        navigate(`/onlyhotel?${params.toString()}`);
                                    }}
                                    className="w-full mt-4 py-2.5 bg-gradient-to-r from-primary to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all text-sm flex items-center justify-center gap-1.5"
                                >
                                    <span className="material-icons-round text-sm">search</span>
                                    {language === 'sv' ? `Sök alla hotell i ${restaurant.destinationName}` : language === 'da' ? `Søg alle hoteller i ${restaurant.destinationName}` : `Search all hotels in ${restaurant.destinationName}`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
                    onClick={() => setLightboxOpen(false)}
                >
                    {/* Close button */}
                    <button
                        onClick={() => setLightboxOpen(false)}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center z-10"
                    >
                        <span className="material-icons-round">close</span>
                    </button>

                    {/* Image counter */}
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/10 text-white text-sm rounded-full">
                        {activeImage + 1} / {allImages.length}
                    </div>

                    {/* Main Image */}
                    <img
                        src={allImages[activeImage]}
                        alt={restaurant.name}
                        className="max-w-[90vw] max-h-[85vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Prev/Next */}
                    {allImages.length > 1 && (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); setActiveImage((activeImage - 1 + allImages.length) % allImages.length); }}
                                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                            >
                                <span className="material-icons-round">chevron_left</span>
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); setActiveImage((activeImage + 1) % allImages.length); }}
                                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                            >
                                <span className="material-icons-round">chevron_right</span>
                            </button>
                        </>
                    )}

                    {/* Thumbnail strip */}
                    {allImages.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] px-4 py-2">
                            {allImages.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={(e) => { e.stopPropagation(); setActiveImage(i); }}
                                    className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${
                                        i === activeImage ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-80'
                                    }`}
                                >
                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}



            {/* Reservation Modal */}
            {showReservationForm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowReservationForm(false)}>
                    <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl animate-fade-in-up" onClick={e => e.stopPropagation()}>
                        <div className="bg-primary p-5 sm:p-6 text-white">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold">{language === 'sv' ? 'Boka bord' : language === 'da' ? 'Reserver bord' : 'Reserve a Table'}</h3>
                                <button onClick={() => setShowReservationForm(false)} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
                                    <span className="material-icons-round text-sm">close</span>
                                </button>
                            </div>
                            <p className="text-white/80 text-sm mt-1">{restaurant.name}</p>
                        </div>
                        {reservationSent ? (
                            <div className="p-8 text-center">
                                <span className="material-icons-round text-green-500 text-5xl mb-4">check_circle</span>
                                <h4 className="text-xl font-bold text-gray-900 mb-2">{language === 'sv' ? 'Bokning skickad!' : language === 'da' ? 'Reservation sendt!' : 'Reservation sent!'}</h4>
                                <p className="text-gray-600">{language === 'sv' ? 'Restaurangen kommer kontakta dig.' : language === 'da' ? 'Restauranten vil kontakte dig.' : 'The restaurant will contact you.'}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleReservationSubmit} className="p-5 sm:p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-1 block">{language === 'sv' ? 'Namn' : language === 'da' ? 'Navn' : 'Name'}</label>
                                        <input type="text" required value={reservationForm.name} onChange={e => setReservationForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-1 block">{language === 'sv' ? 'Gäster' : language === 'da' ? 'Gæster' : 'Guests'}</label>
                                        <input type="number" min={1} max={20} required value={reservationForm.guests} onChange={e => setReservationForm(p => ({ ...p, guests: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                                    <input type="email" required value={reservationForm.email} onChange={e => setReservationForm(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-1 block">{language === 'sv' ? 'Datum' : language === 'da' ? 'Dato' : 'Date'}</label>
                                        <input type="date" required value={reservationForm.date} onChange={e => setReservationForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-1 block">{language === 'sv' ? 'Tid' : language === 'da' ? 'Tid' : 'Time'}</label>
                                        <input type="time" required value={reservationForm.time} onChange={e => setReservationForm(p => ({ ...p, time: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">{language === 'sv' ? 'Anteckningar' : language === 'da' ? 'Noter' : 'Notes'}</label>
                                    <textarea value={reservationForm.notes} onChange={e => setReservationForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none" />
                                </div>
                                <button type="submit" className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                                    <span className="material-icons-round">send</span>
                                    {language === 'sv' ? 'Skicka bokning' : language === 'da' ? 'Send reservation' : 'Submit Reservation'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RestaurantPage;
